import React, { useState, useMemo } from "react";
import { Modal } from "antd";
import {
  VideoCameraOutlined,
  VideoCameraFilled,
  EnvironmentOutlined,
  WifiOutlined,
  SearchOutlined,
  AppstoreOutlined,
  ExpandOutlined,
  CompressOutlined,
  CloseOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  DeleteOutlined,
  DollarCircleOutlined,
  SwapOutlined,
  FireOutlined,
  DownOutlined,
  ReloadOutlined,
  DisconnectOutlined,
} from "@ant-design/icons";

const STORES = ["Indiranagar", "Koramangala", "Whitefield", "HSR Layout", "Jayanagar"];

const EVENT_TYPES = {
  mobile: { label: "Mobile usage", icon: <MobileOutlined />, tone: "red" },
  hygiene: { label: "Hygiene violation", icon: <SafetyCertificateOutlined />, tone: "amber" },
  kabab: { label: "Kabab-station alert", icon: <FireOutlined />, tone: "ember" },
  cash: { label: "Cash-counter issue", icon: <DollarCircleOutlined />, tone: "red" },
  entry: { label: "Entry spike", icon: <SwapOutlined />, tone: "blue" },
  wastage: { label: "Wastage event", icon: <DeleteOutlined />, tone: "amber" },
};

const CAMERAS = [
  { id: "CAM-01", store: "Indiranagar", area: "Entrance", status: "online", event: "entry" },
  { id: "CAM-02", store: "Indiranagar", area: "Kitchen", status: "online", event: null },
  { id: "CAM-03", store: "Indiranagar", area: "Billing counter", status: "online", event: null },
  { id: "CAM-09", store: "Indiranagar", area: "Kabab station", status: "online", event: "kabab" },
  { id: "CAM-04", store: "Koramangala", area: "Entrance", status: "online", event: null },
  { id: "CAM-05", store: "Koramangala", area: "Kitchen", status: "offline", event: null },
  { id: "CAM-06", store: "Koramangala", area: "Billing counter", status: "online", event: "mobile" },
  { id: "CAM-07", store: "Koramangala", area: "Dining area", status: "online", event: null },
  { id: "CAM-08", store: "Whitefield", area: "Entrance", status: "online", event: null },
  { id: "CAM-10", store: "Whitefield", area: "Kitchen", status: "online", event: "wastage" },
  { id: "CAM-11", store: "Whitefield", area: "Billing counter", status: "online", event: null },
  { id: "CAM-12", store: "HSR Layout", area: "Entrance", status: "online", event: null },
  { id: "CAM-13", store: "HSR Layout", area: "Kitchen", status: "offline", event: null },
  { id: "CAM-14", store: "HSR Layout", area: "Billing counter", status: "online", event: "cash" },
  { id: "CAM-15", store: "Jayanagar", area: "Entrance", status: "online", event: null },
  { id: "CAM-16", store: "Jayanagar", area: "Kitchen", status: "online", event: null },
  { id: "CAM-17", store: "Jayanagar", area: "Dishwash", status: "offline", event: "hygiene" },
];

const RECENT_EVENTS = [
  { id: "E1", cameraId: "CAM-06", type: "mobile", time: "9:51 PM", detail: "Captain seen on phone at billing counter" },
  { id: "E2", cameraId: "CAM-17", type: "hygiene", time: "9:22 PM", detail: "Surface not sanitized between uses" },
  { id: "E3", cameraId: "CAM-09", type: "kabab", time: "8:58 PM", detail: "Idle-flame time exceeded 4 min" },
  { id: "E4", cameraId: "CAM-14", type: "cash", time: "8:40 PM", detail: "Cash drawer opened without matching bill scan" },
  { id: "E5", cameraId: "CAM-01", type: "entry", time: "8:15 PM", detail: "Unusual entry spike detected" },
  { id: "E6", cameraId: "CAM-10", type: "wastage", time: "7:52 PM", detail: "Excess food discard flagged" },
];

const TIMELINE_HOURS = ["10am", "11am", "12pm", "1pm", "2pm", "3pm", "4pm", "5pm", "6pm", "7pm", "8pm", "9pm"];

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

const toneMap = {
  blue: { text: "text-[#2F6FED]", bg: "bg-[#2F6FED]/10", ring: "ring-[#2F6FED]/20", solid: "#2F6FED" },
  ember: { text: "text-[#C2650B]", bg: "bg-[#C2650B]/10", ring: "ring-[#C2650B]/20", solid: "#C2650B" },
  amber: { text: "text-[#B45309]", bg: "bg-[#FFFBEB]", ring: "ring-[#FDE68A]", solid: "#D97706" },
  red: { text: "text-[#B42318]", bg: "bg-[#FEF2F2]", ring: "ring-[#FECACA]", solid: "#DC2626" },
};

function Card({ className = "", children }) {
  return (
    <div className={`rounded-2xl border border-[#E6EAF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ${className}`}>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, icon }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none rounded-lg border border-[#E1E5EC] bg-white pl-8 pr-7 py-2 text-sm text-[#3B4152] shadow-sm hover:border-[#C7CCD9] focus:outline-none focus:ring-2 focus:ring-[#2F6FED]/30 cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A90A3] text-xs pointer-events-none">{icon}</span>
      <DownOutlined className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[9px] text-[#8A90A3] pointer-events-none" />
    </div>
  );
}

// Illustrated camera feed — camera icon placeholder (no people) + optional flagged-event badge
function CameraVisual({ camera, height = "h-40" }) {
  if (camera.status === "offline") {
    return (
      <div className={`relative w-full ${height} rounded-xl bg-[#1B212B] flex flex-col items-center justify-center gap-2`}>
        <DisconnectOutlined className="text-[#5B6272] text-xl" />
        <p className="text-xs text-[#8A90A3] font-medium">Camera offline</p>
      </div>
    );
  }

  const ev = camera.event ? EVENT_TYPES[camera.event] : null;
  const t = ev ? toneMap[ev.tone] : null;

  return (
    <div className={`relative w-full ${height} rounded-xl overflow-hidden bg-gradient-to-b from-[#232A38] to-[#171B24] flex items-center justify-center`}>
      {/* Subtle scanline / vignette background to sell the "feed" look */}
      <svg viewBox="0 0 320 160" className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="xMidYMax slice">
        <rect x="0" y="128" width="320" height="12" fill="#0F131B" />
        <rect x="0" y="124" width="320" height="6" fill="#2B3341" />
      </svg>

      {/* Camera icon placeholder in the middle of the feed */}
      <div
        className={`relative flex items-center justify-center rounded-full ${
          ev ? "" : "bg-white/5"
        }`}
        style={{
          width: "22%",
          aspectRatio: "1 / 1",
          minWidth: 40,
          border: ev ? `2px solid ${t.solid}` : "2px solid rgba(255,255,255,0.12)",
        }}
      >
        <VideoCameraFilled style={{ fontSize: "1.6rem", color: ev ? t.solid : "#8B93A3" }} />
      </div>

      {ev && (
        <span
          className="absolute left-1/2 top-3 -translate-x-1/2 whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-semibold text-white shadow"
          style={{ backgroundColor: t.solid }}
        >
          {ev.label}
        </span>
      )}
      <span className="absolute top-2 left-2 flex items-center gap-1 text-[10px] text-white/85">
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#34D399] opacity-75" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#34D399]" />
        </span>
        LIVE
      </span>
    </div>
  );
}

function StatusDot({ status }) {
  return <span className={`h-2 w-2 rounded-full ${status === "online" ? "bg-[#12B76A]" : "bg-[#DC2626]"}`} />;
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function LiveCctvMonitoring() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [filters, setFilters] = useState({ store: "all", status: "all" });
  const [search, setSearch] = useState("");

  const onlineCount = CAMERAS.filter((c) => c.status === "online").length;

  const filteredCameras = useMemo(() => {
    return CAMERAS.filter((c) => {
      if (filters.store !== "all" && c.store !== filters.store) return false;
      if (filters.status !== "all" && c.status !== filters.status) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!`${c.id} ${c.area} ${c.store}`.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [filters, search]);

  const selectedCamera = CAMERAS.find((c) => c.id === selectedId) || null;

  const openCamera = (id) => {
    setSelectedId(id);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFullscreen(false);
  };

  const cameraEvents = selectedCamera ? RECENT_EVENTS.filter((e) => e.cameraId === selectedCamera.id) : [];

  return (
    <div className="min-h-screen w-full bg-[#F6F8FB] text-[#12172B] font-sans">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500&display=swap');
        .font-sans { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
        .font-mono-num { font-family: 'JetBrains Mono', ui-monospace, monospace; }
      `}</style>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* ---------------- Header ---------------- */}
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#2F6FED] to-[#1E4FBE] flex items-center justify-center shadow-md shadow-[#2F6FED]/25">
              <VideoCameraOutlined className="text-white text-lg" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight text-[#0B0F1F] leading-none">Live CCTV / Camera Monitoring</h1>
              <p className="text-xs text-[#8A90A3] mt-1">CogniVision · Empire Restaurant · {onlineCount}/{CAMERAS.length} cameras online</p>
            </div>
          </div>
        </header>

        {/* ---------------- Filter bar ---------------- */}
        <Card className="p-4 mb-5">
          <div className="flex flex-wrap items-center gap-3">
            <Select
              icon={<WifiOutlined />}
              value={filters.status}
              onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
              options={[{ value: "all", label: "All statuses" }, { value: "online", label: "Online" }, { value: "offline", label: "Offline" }]}
            />
            <div className="relative flex-1 min-w-[200px]">
              <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A90A3] text-sm" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search camera ID, area, store…"
                className="w-full rounded-lg border border-[#E1E5EC] bg-white pl-9 pr-3 py-2 text-sm text-[#12172B] placeholder:text-[#A0A6B5] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2F6FED]/30"
              />
            </div>
            {(filters.store !== "all" || filters.status !== "all" || search) && (
              <button
                onClick={() => { setFilters({ store: "all", status: "all" }); setSearch(""); }}
                className="flex items-center gap-1.5 rounded-lg border border-[#E1E5EC] bg-white px-3 py-2 text-sm text-[#5B6272] hover:border-[#C7CCD9] shadow-sm"
              >
                <ReloadOutlined className="text-xs" /> Clear
              </button>
            )}
          </div>
        </Card>

        {/* ---------------- Grid ---------------- */}
        <section className="grid grid-cols-1 gap-5">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-[#12172B]">Multi-camera grid</p>
              <p className="text-xs text-[#8A90A3]">{filteredCameras.length} camera{filteredCameras.length !== 1 ? "s" : ""}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredCameras.map((c) => (
                <button key={c.id} onClick={() => openCamera(c.id)} className="text-left group">
                  <CameraVisual camera={c} />
                  <div className="flex items-center justify-between mt-2 px-0.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#12172B] truncate group-hover:text-[#2F6FED]">{c.id} · {c.area}</p>
                      <p className="text-xs text-[#8A90A3] truncate">{c.store}</p>
                    </div>
                    <StatusDot status={c.status} />
                  </div>
                </button>
              ))}
              {filteredCameras.length === 0 && (
                <p className="col-span-full text-center text-sm text-[#A0A6B5] py-10">No cameras match the current filters.</p>
              )}
            </div>
          </Card>
        </section>
      </div>

      {/* ---------------- Live stream modal ---------------- */}
      <Modal
        open={modalOpen && !fullscreen}
        onCancel={closeModal}
        footer={null}
        width={760}
        title={
          selectedCamera ? (
            <div className="flex items-center gap-2">
              <VideoCameraOutlined className="text-[#2F6FED]" />
              <span>{selectedCamera.id} · {selectedCamera.area}</span>
            </div>
          ) : null
        }
        destroyOnClose
      >
        {selectedCamera && (
          <div>
            <CameraVisual camera={selectedCamera} height="h-[360px]" />
          </div>
        )}
      </Modal>

      {/* ---------------- Fullscreen viewer (launched from the modal) ---------------- */}
      {fullscreen && selectedCamera && (
        <div className="fixed inset-0 z-[1001] bg-[#0B0F1F] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div>
              <p className="text-white font-semibold">{selectedCamera.id} · {selectedCamera.area}</p>
              <p className="text-xs text-white/60 flex items-center gap-1.5 mt-1"><EnvironmentOutlined /> {selectedCamera.store}</p>
            </div>
            <button
              onClick={() => setFullscreen(false)}
              className="flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-2 text-xs font-medium text-white"
            >
              <CompressOutlined /> Exit full screen
            </button>
          </div>
          <div className="flex-1 p-6 flex items-center justify-center">
            <div className="w-full max-w-5xl">
              <CameraVisual camera={selectedCamera} height="h-[70vh]" />
            </div>
          </div>
          <button onClick={() => setFullscreen(false)} className="absolute top-4 right-4 sm:hidden text-white">
            <CloseOutlined />
          </button>
        </div>
      )}
    </div>
  );
}